/**
 * Knack video reviews carousel.
 *
 * A <knack-video-reviews> custom element wrapping a native scroll-snap track. Keeps JS minimal:
 *
 *  - Prev / next pill buttons page the track by roughly one card and disable at each end.
 *  - The controls are revealed only when the cards actually overflow (otherwise there is nothing
 *    to scroll, so no controls). Hidden by default in markup to avoid a flash of unusable buttons.
 *  - Click-to-play is lazy. The poster + play button show first; pressing play loads the heavy
 *    media: a hosted <video> swaps in (muted + playsinline, with native controls) and plays, or
 *    an external embed is injected as an autoplaying iframe. Pressing again toggles play / pause
 *    for hosted video.
 *
 * The track itself is natively keyboard-scrollable (tabindex="0" + arrow keys) and swipeable, so
 * no custom drag handling is needed. Smooth scrolling is a CSS concern gated on
 * prefers-reduced-motion; this script only sets scrollLeft and lets CSS decide how to animate it.
 *
 * Being a custom element, it re-initialises automatically when the Theme Editor re-renders the
 * section (a fresh element is connected), so no shopify:section:load wiring is required.
 */
class KnackVideoReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-knack-vr-track]');
    this.controls = this.querySelector('[data-knack-vr-controls]');
    this.prevBtn = this.querySelector('[data-knack-vr-prev]');
    this.nextBtn = this.querySelector('[data-knack-vr-next]');

    if (this.track) {
      this.setupControls();
      this.setupPlayers();
    }
  }

  disconnectedCallback() {
    if (this._onScroll) this.track.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  /* ---- Prev / next paging ---- */

  setupControls() {
    if (!this.controls || !this.prevBtn || !this.nextBtn) return;

    this.prevBtn.addEventListener('click', () => this.page(-1));
    this.nextBtn.addEventListener('click', () => this.page(1));

    // Keep the disabled state and visibility in sync with the scroll position.
    this._onScroll = () => this.updateControls();
    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    this._onResize = () => this.updateControls();
    window.addEventListener('resize', this._onResize, { passive: true });

    this.updateControls();
  }

  // Distance to advance: one card plus the gap, falling back to ~80% of the viewport.
  pageDistance() {
    const firstItem = this.track.querySelector('.knack-vr__item');
    if (firstItem) {
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return firstItem.getBoundingClientRect().width + gap;
    }
    return this.track.clientWidth * 0.8;
  }

  page(direction) {
    this.track.scrollLeft += direction * this.pageDistance();
  }

  updateControls() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const overflows = maxScroll > 1;

    // Only surface the controls when there is something to scroll.
    this.controls.hidden = !overflows;
    if (!overflows) return;

    // 1px tolerance for sub-pixel rounding at the extremities.
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;

    this.prevBtn.disabled = atStart;
    this.nextBtn.disabled = atEnd;
  }

  /* ---- Lazy click-to-play ---- */

  setupPlayers() {
    this.querySelectorAll('[data-knack-vr-player]').forEach((media) => {
      const playBtn = media.querySelector('[data-knack-vr-play]');
      if (!playBtn) return;

      playBtn.addEventListener('click', () => this.activate(media));
    });
  }

  activate(media) {
    const type = media.dataset.type;

    if (type === 'file') {
      const video = media.querySelector('[data-knack-vr-video]');
      if (!video) return;

      media.classList.add('is-playing');
      // preload="none" means the source is only fetched now, on first intent.
      video.removeAttribute('tabindex');
      const playback = video.play();
      if (playback && typeof playback.catch === 'function') {
        playback.catch(() => {
          // Autoplay can be blocked; native controls remain available to start manually.
        });
      }
      video.focus({ preventScroll: true });
      return;
    }

    if (type === 'embed') {
      if (media.dataset.activated === 'true') return;
      const src = media.dataset.embedSrc;
      if (!src) return;

      // Reuse the (already-localised) play button label so the iframe has an accessible name.
      const playBtn = media.querySelector('[data-knack-vr-play]');
      const title = (playBtn && playBtn.getAttribute('aria-label')) || '';

      const iframe = document.createElement('iframe');
      iframe.className = 'knack-vr-card__embed';
      iframe.setAttribute('src', this.embedSrc(src));
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', title);
      iframe.style.position = 'absolute';
      iframe.style.inset = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = '0';
      iframe.style.zIndex = '1';

      media.appendChild(iframe);
      media.dataset.activated = 'true';
      media.classList.add('is-playing');
    }
  }

  // Append an autoplay flag to the provider URL without clobbering existing query params.
  embedSrc(src) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('autoplay', '1');
      if (/youtube|youtu\.be/.test(url.hostname)) {
        url.searchParams.set('rel', '0');
        url.searchParams.set('playsinline', '1');
      }
      if (/vimeo/.test(url.hostname)) {
        url.searchParams.set('playsinline', '1');
      }
      return url.toString();
    } catch (e) {
      return src;
    }
  }
}

if (!customElements.get('knack-video-reviews')) {
  customElements.define('knack-video-reviews', KnackVideoReviews);
}
